import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {Action, select, Store} from '@ngrx/store';
import {getCommandQueue, getCommandStack, getQueryQueue, getQueryStack, getSyncActive} from './reducer';
import {Observable} from 'rxjs';
import {flatMap, withLatestFrom} from 'rxjs/operators';
import * as actions from './actions';
import {Dictionary, Request} from './model';
import {NgrxManagerService} from './ngrx-manager.service';

@Injectable()
export class NgrxManagerEffects {
  // QUEUES
  @Effect()
  queryQueueNextAction$: Observable<Action> = this.actions$.pipe(
    ofType<actions.QueryQueueNextAction>(actions.QUERY_QUEUE_NEXT)
    , withLatestFrom(this.store.pipe(select(getQueryQueue)), this.store.pipe(select(getQueryStack)), this.store.pipe(select(getSyncActive)), this.store.pipe(select(getCommandStack)), this.store.pipe(select(getCommandQueue)))
    , flatMap(([x, queryQueue, queryStack, syncActive, commandStack, commandQueue]: [actions.QueryQueueNextAction, Dictionary<Request>, Dictionary<Request>, boolean, Dictionary<Request>, Array<Request>]) => {
      const res = [];

      if (syncActive) {
        const queryQueueTmp = queryQueue.values();
        const request = queryQueueTmp.shift();
        if (request !== null && request !== undefined && request.action !== undefined) {
          res.push(request.action);
          res.unshift(new actions.DeleteQueryQueueRequestAction(request, 0));
        }

        if (commandStack._values.length === 0 && commandQueue.length > 0) {
          res.push(new actions.CommandQueueNextAction());
        }
      }

      return res;
    })
  );

  // COMMANDS
  @Effect()
  commandQueueNextAction$: Observable<Action> = this.actions$.pipe(
    ofType<actions.CommandQueueNextAction>(actions.COMMAND_QUEUE_NEXT)
    , withLatestFrom(this.store.pipe(select(getCommandQueue)), this.store.pipe(select(getSyncActive)))
    , flatMap(([x, commandQueue, syncActive]: [actions.CommandQueueNextAction, Array<Request>, boolean]) => {
      const res = [];
      let request = null;
      if (syncActive) {
        if (commandQueue.length > 0) {
          request = commandQueue.shift();
        }

        if (request !== null && request !== undefined && request.action !== undefined && request.action !== null) {
          res.push(request.action);
          res.unshift(new actions.DeleteCommandQueueRequestAction(request, 0));
        }
      }

      return res;
    })
  );

  // OTHER
  @Effect()
  startSyncAction$: Observable<Action> = this.actions$.pipe(
    ofType<actions.StartSyncAction>(actions.START_SYNC)
    , flatMap((x) => {
      if (x.checkInternet) {
        this.ngrxManagerService.checkInternetConnection(true);
        return [];
      } else {
        return [
          // Erst bereinigen, falls etwas gehangen hat
          new actions.CommandStackCleanUpAction(),
          new actions.QueryStackCleanUpAction(),
          // Mit der Abarbeitung starten
          new actions.CommandQueueNextAction(), // Erst die Commands, das die Queries beim abarbeiten die Commands ggf. mit anstoÃen
          new actions.QueryQueueNextAction()
        ];
      }
    })
  );

  @Effect()
  stopSyncAction$: Observable<Action> = this.actions$.pipe(
    ofType<actions.StopSyncAction>(actions.STOP_SYNC)
    , flatMap((x) => {
      return [
        new actions.QueryStackCleanUpAction(),
        new actions.CommandStackCleanUpAction()
      ];
    })
  );

  // ADMIN
  // SEND ACTION
  @Effect()
  sendQueryStackRequestAction$: Observable<Action> = this.actions$.pipe(
    ofType<actions.SendQueryStackRequestAction>(actions.SEND_QUERY_STACK_REQUEST)
    , flatMap((x: actions.SendQueryStackRequestAction) => {
      const res = [];
      res.push(new actions.QueryStackRemoveAction(x.request.key));
      res.push(x.request.action);
      return res;
    })
  );

  @Effect()
  sendQueryQueueRequestAction$: Observable<Action> = this.actions$.pipe(
    ofType<actions.SendQueryQueueRequestAction>(actions.SEND_QUERY_QUEUE_REQUEST)
    , flatMap((x: actions.SendQueryQueueRequestAction) => {
      const res = [];
      res.push(new actions.QueryQueueRemoveAction(x.request.key));
      res.push(x.request.action);
      return res;
    })
  );

  @Effect()
  sendQueryFailedRequestAction$: Observable<Action> = this.actions$.pipe(
    ofType<actions.SendQueryFailedRequestAction>(actions.SEND_QUERY_FAILED_REQUEST)
    , flatMap((x: actions.SendQueryFailedRequestAction) => {
      const res = [];
      res.push(new actions.DeleteQueryFailedRequestAction(x.request, x.index));
      res.push(x.request.action);
      return res;
    })
  );

  @Effect()
  sendCommandStackRequestAction$: Observable<Action> = this.actions$.pipe(
    ofType<actions.SendCommandStackRequestAction>(actions.SEND_COMMAND_STACK_REQUEST)
    , flatMap((x: actions.SendCommandStackRequestAction) => {
      const res = [];
      res.push(new actions.CommandStackRemoveAction(x.request.key));
      res.push(x.request.action);
      return res;
    })
  );

  @Effect()
  sendCommandQueueRequestAction$: Observable<Action> = this.actions$.pipe(
    ofType<actions.SendCommandQueueRequestAction>(actions.SEND_COMMAND_QUEUE_REQUEST)
    , flatMap((x: actions.SendCommandQueueRequestAction) => {
      const res = [];
      res.push(new actions.DeleteCommandQueueRequestAction(x.request, x.index));
      res.push(x.request.action);
      return res;
    })
  );

  @Effect()
  sendCommandFailedRequestAction$: Observable<Action> = this.actions$.pipe(
    ofType<actions.SendCommandFailedRequestAction>(actions.SEND_COMMAND_FAILED_REQUEST)
    , flatMap((x: actions.SendCommandFailedRequestAction) => {
      const res = [];
      res.push(new actions.DeleteCommandFailedRequestAction(x.request, x.index));
      res.push(x.request.action);
      return res;
    })
  );

  constructor(private actions$: Actions,
              private store: Store<any>,
              private ngrxManagerService: NgrxManagerService) {
  }
}
